import { versionedNamespace } from './versionedNamespace';

export const NAMESPACE = 'wds';

type ClassValue = string | false | null | undefined;
type VariantMap = Record<string, readonly string[]>;

type NodeConfig = {
  variants?: VariantMap;
  states?: readonly string[];
};

type Config = Record<string, NodeConfig>;

type NodeProps<Node extends NodeConfig> = {
  [V in keyof NonNullable<Node['variants']>]?:
    | NonNullable<Node['variants']>[V][number]
    | false
    | null;
} & {
  [S in NonNullable<Node['states']>[number] & string]?: boolean | null;
};

type ClassNameFn<Node extends NodeConfig> = (
  props?: NodeProps<Node>,
  ...classes: ClassValue[]
) => string;

type ClassNames<C extends Config> = {
  [K in keyof C | 'root']: ClassNameFn<K extends keyof C ? C[K] : NodeConfig>;
};

const kebab = (value: string) =>
  value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

const join = (...classes: ClassValue[]) => classes.filter(Boolean).join(' ');

const modifierTails = (node: NodeConfig, props: Record<string, unknown>) => {
  const variants = Object.entries(node.variants ?? {})
    .map(([name, values]) => {
      const value = props[name] as string | false | null | undefined;
      if (!value || !values.includes(value)) return null;
      return `--${kebab(name)}-${kebab(value)}`;
    })
    .filter(Boolean) as string[];

  const states = (node.states ?? [])
    .filter(name => props[name])
    .map(name => `--${kebab(name)}`);

  return [...variants, ...states];
};

export const createClassNames = <const C extends Config = Record<never, never>>(
  blockName: string,
  config: C = {} as C,
  versionedNs: string = versionedNamespace,
): ClassNames<C> => {
  const namespaces = versionedNs ? [NAMESPACE, versionedNs] : [NAMESPACE];

  const nodeClassNames =
    (base: string, node: NodeConfig) =>
    (props: Record<string, unknown> = {}, ...classes: ClassValue[]) => {
      const tails = ['', ...modifierTails(node, props)];
      return join(
        ...namespaces.flatMap(ns => tails.map(tail => `${ns}-${base}${tail}`)),
        ...classes,
      );
    };

  const slots = Object.fromEntries(
    Object.entries(config)
      .filter(([name]) => name !== 'root')
      .map(([name, node]) => [
        name,
        nodeClassNames(`${blockName}__${kebab(name)}`, node ?? {}),
      ]),
  );

  return {
    ...slots,
    root: nodeClassNames(blockName, config.root ?? {}),
  } as ClassNames<C>;
};

export const defineClassNames = <const C extends Config = Record<never, never>>(
  blockName: string,
  config: C = {} as C,
): ClassNames<C> => createClassNames(blockName, config);
